import { Core, Decorative } from "@faire/design-tokens";
import { trackBrandLeadsBrandsPageHeaderSectionView } from "@faire/web-api/events/brandLeads/view/brandsPageHeaderSection";
import { trackR2BReferralsClicksOnSignUpToSellButtonClick } from "@faire/web-api/events/r2BReferrals/click/clicksOnSignUpToSellButton";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";

const TopSectionBase: React.FC = () => {
  useEffect(() => {
    trackBrandLeadsBrandsPageHeaderSectionView();
  }, []);

  return (
    <Column
      style={{
        backgroundColor: Decorative.shade.yellow,
        width: "100%",
      }}
    >
      <HeaderContent />
      <Spacer height="7x" />
      <ResponsiveContentV2 desktopAndAbove={<Spacer height="7x" />} />
    </Column>
  );
};

export const TopSection = observer(TopSectionBase);

const SignUpToSellButton: React.FC = () => {
  return (
    <SignUpButton onClick={trackR2BReferralsClicksOnSignUpToSellButtonClick}>
      <StrictLocalMsg
        defaultMessage="Sign up to sell"
        description={{
          text: "Prompts the brand to sign up for Faire.",
        }}
      />
    </SignUpButton>
  );
};

const CTAButtons: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={<SignUpToSellButton />}
      desktopAndAbove={
        <Row>
          <SignUpToSellButton />
        </Row>
      }
    />
  );
};

const TitleText = () => {
  return (
    <StrictLocalMsg
      defaultMessage="Claim your offer to get one-on-one assistance setting up your shop"
      description={{
        text: "Promotional message imploring brands to claim their limited time offer and get personal assistance from Faire while onboarding.",
      }}
    />
  );
};

const MobileTitle: React.FC = () => {
  return (
    <Column align="flex-start">
      <Typography variant="displaySSerifRegular" color={Core.text.inverse}>
        <TitleText />
      </Typography>
      <Spacer height="2x" />
      <CTAButtons />
    </Column>
  );
};

const BrandsDesktopTitle: React.FC = () => {
  return (
    <Column align="center">
      <DesktopTitle
        variant="displayLSerifRegular"
        color={Core.text.inverse}
        align="center"
      >
        <TitleText />
      </DesktopTitle>
      <Spacer height="3x" />
      <CTAButtons />
    </Column>
  );
};

const BrandsPageTitle: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={<MobileTitle />}
      desktopAndAbove={<BrandsDesktopTitle />}
    />
  );
};

const HeaderContentBase: React.FC = () => {
  return (
    <>
      <BrandsPageTitleWrapper>
        <BrandsPageTitle />
      </BrandsPageTitleWrapper>
    </>
  );
};

const HeaderContent = observer(HeaderContentBase);

const DesktopTitle = styled(Typography)`
  max-width: 780px;
`;

const BrandsPageTitleWrapper = styled(Column).attrs({
  justify: { mobileAndAbove: "left", tabletAndAbove: "center" },
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
})`
  width: 100%;
  padding: ${getSpacing(0, "3x")};
`;

styled(Button).attrs({
  variant: "secondary",
  target: "_blank",
})`
  width: max-content;
  color: ${Core.text.default};
  background-color: ${Core.surface.inverse};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
`;

const SignUpButton = styled(Button).attrs({
  variant: "secondary",
  target: "_blank",
})`
  width: max-content;
  color: ${Core.text.inverse};
  background-color: transparent;
  border: 1px solid ${Core.border.muted};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
`;
